import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { api } from '../services/apiClient';
import { API_ENDPOINTS } from '../config/api';

interface Site {
  id: number;
  name: string;
  subdomain: string | null;
  is_default: boolean;
}

interface User {
  user_id: number;
  email: string;
  full_name: string;
  site_id: number;
  role_name: string | null;
  permissions: string[];
}

interface AuthState {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  siteId: number | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  // step-1 state: user verified but site not yet selected
  pendingEmail: string | null;
  availableSites: Site[];
  requiresSiteSelection: boolean;

  // Actions
  login: (email: string, password: string) => Promise<void>;
  loginWithGoogle: (code: string, redirectUri: string) => Promise<void>;
  selectSite: (siteId: number) => Promise<void>;
  logout: () => void;
  refreshAccessToken: () => Promise<void>;
  clearError: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      siteId: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
      pendingEmail: null,
      availableSites: [],
      requiresSiteSelection: false,

      loginWithGoogle: async (code: string, redirectUri: string) => {
        set({ isLoading: true, error: null });
        try {
          const response = await api.post<any>(API_ENDPOINTS.AUTH.GOOGLE, { code, redirect_uri: redirectUri });
          if (response.requires_site_selection) {
            set({ pendingEmail: response.email, availableSites: response.available_sites, requiresSiteSelection: true, isLoading: false });
            return;
          }
          const user: User = {
            user_id: response.user_id, email: response.email, full_name: response.full_name,
            site_id: response.site_id, role_name: response.role_name, permissions: response.permissions ?? [],
          };
          localStorage.setItem('access_token', response.access_token);
          localStorage.setItem('refresh_token', response.refresh_token);
          set({ accessToken: response.access_token, refreshToken: response.refresh_token, user, siteId: response.site_id, isAuthenticated: true, requiresSiteSelection: false, pendingEmail: null, availableSites: [], isLoading: false });
        } catch (err: any) {
          set({ error: err?.response?.data?.error?.message ?? err?.message ?? 'Google login failed', isLoading: false });
        }
      },

      login: async (email: string, password: string) => {
        set({ isLoading: true, error: null });
        try {
          const response = await api.post<any>(API_ENDPOINTS.AUTH.LOGIN, { email, password });

          if (response.requires_site_selection) {
            set({
              pendingEmail: email,
              availableSites: response.available_sites,
              requiresSiteSelection: true,
              isLoading: false,
            });
            return;
          }

          // Single site — tokens returned directly
          const user: User = {
            user_id: response.user_id,
            email: response.email,
            full_name: response.full_name,
            site_id: response.site_id,
            role_name: response.role_name,
            permissions: response.permissions ?? [],
          };

          localStorage.setItem('access_token', response.access_token);
          localStorage.setItem('refresh_token', response.refresh_token);

          set({
            accessToken: response.access_token,
            refreshToken: response.refresh_token,
            user,
            siteId: response.site_id,
            isAuthenticated: true,
            requiresSiteSelection: false,
            pendingEmail: null,
            availableSites: [],
            isLoading: false,
          });
        } catch (err: any) {
          const message = err?.response?.data?.error?.message ?? err?.message ?? 'Login failed';
          set({ error: message, isLoading: false });
        }
      },

      selectSite: async (siteId: number) => {
        const { pendingEmail } = get();
        if (!pendingEmail) {
          set({ error: 'No pending login session' });
          return;
        }
        set({ isLoading: true, error: null });
        try {
          const response = await api.post<any>(API_ENDPOINTS.AUTH.LOGIN_COMPLETE, {
            email: pendingEmail,
            site_id: siteId,
          });

          const user: User = {
            user_id: response.user_id,
            email: response.email,
            full_name: response.full_name,
            site_id: response.site_id,
            role_name: response.role_name,
            permissions: response.permissions ?? [],
          };

          localStorage.setItem('access_token', response.access_token);
          localStorage.setItem('refresh_token', response.refresh_token);

          set({
            accessToken: response.access_token,
            refreshToken: response.refresh_token,
            user,
            siteId: response.site_id,
            isAuthenticated: true,
            requiresSiteSelection: false,
            pendingEmail: null,
            availableSites: [],
            isLoading: false,
          });
        } catch (err: any) {
          const message = err?.response?.data?.error?.message ?? err?.message ?? 'Site selection failed';
          set({ error: message, isLoading: false });
        }
      },

      logout: () => {
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          siteId: null,
          isAuthenticated: false,
          pendingEmail: null,
          availableSites: [],
          requiresSiteSelection: false,
          error: null,
        });
      },

      refreshAccessToken: async () => {
        const { refreshToken } = get();
        if (!refreshToken) { get().logout(); return; }
        try {
          const response = await api.post<any>(API_ENDPOINTS.AUTH.REFRESH, { refresh_token: refreshToken });
          localStorage.setItem('access_token', response.access_token);
          if (response.refresh_token) localStorage.setItem('refresh_token', response.refresh_token);
          set({
            accessToken: response.access_token,
            refreshToken: response.refresh_token ?? refreshToken,
          });
        } catch {
          get().logout();
        }
      },

      clearError: () => set({ error: null }),
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        siteId: state.siteId,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);
